import { ROUTES } from '@/urls';
import { DsIconName } from '@/components/ds/icons';

// Single source of truth for the primary sidebar navigation (order preserved
// from the original navbar). `icon` names map to the design-system icon set.
export interface NavLinkModel {
  route: string;
  icon: DsIconName;
  label: string;
  dataIntro: string;
}

export const NAV_LINKS: NavLinkModel[] = [
  {
    route: ROUTES.DASHBOARD,
    icon: 'dashboard',
    label: 'Dashboard',
    dataIntro:
      'Dashboard is your starting point. In Action Center you find notifications on issues that require your attention or action - click "View" to see data behind the issue, click "Dispute" to start conversation with people accountable for solving the problem. In Disputes Center you have all the ongoing communication threads listed - keep a close watch on new messages and respond promptly. Last but not least, Dashboard delivers brief overview of your cost data.',
  },
  {
    route: ROUTES.ALLOCATION,
    icon: 'allocation',
    label: 'Allocation',
    dataIntro:
      "Allocation tab is where Technical Users (Cloud Account Owners, Resource Owners) perform resource allocation. Your organization's desired state is when each resource has assigned engineer responsible for its management and is allocated to a System, where its cost is recognized.This is prerequisite for effective cost governance and optimization.Business Users(Sponsor, Department Owners, System Owners) track the status of Allocation within their budget scopes.",
  },
  {
    route: ROUTES.ATTESTATION,
    icon: 'attestation',
    label: 'Attestation',
    dataIntro:
      'The Attestation tab is where Resource Owners perform a review of managed resources, validate and confirm the need for their provisioning, or flag resources for decommissioning. Cloud Account Owners and Business Users monitor the status of Attestation efforts within their scopes to ensure timely completion.',
  },
  {
    route: ROUTES.BUDGETS,
    icon: 'budgets',
    label: 'Budgets',
    dataIntro:
      'Budget tab allows Business Users to manage budgets and cascade them to the cost centers they are accountable for - Sponsor distributes Global Budget to Departments, Department Owners distribute Department Budget to Systems. For each managed budget scope annual and monthly spending data and forecasts are available.',
  },
  {
    route: ROUTES.OPTIMIZATION,
    icon: 'optimization',
    label: 'Optimization',
    dataIntro:
      'Optimization tab delivers data on identified saving opportunities. Technical Users review them and decide to implement or reject particular recommendation. Business Users track the status of optimization efforts within their budget scopes.',
  },
  {
    route: ROUTES.REPORTING,
    icon: 'reporting',
    label: 'Reporting',
    dataIntro:
      'Reporting allows you to explore your cost data to get a better understanding of what drives your spending.',
  },
];

// pathname → page title (used by the topbar breadcrumb + heading)
export function pageTitleForPath(pathname: string): string {
  const map: Record<string, string> = {
    '/': 'Dashboard',
    [ROUTES.DASHBOARD]: 'Dashboard',
    [ROUTES.ALLOCATION]: 'Allocation',
    [ROUTES.ATTESTATION]: 'Attestation',
    [ROUTES.BUDGETS]: 'Budgets',
    [ROUTES.OPTIMIZATION]: 'Optimization',
    [ROUTES.REPORTING]: 'Reporting',
    [ROUTES.DISPUTES]: 'Disputes',
    [ROUTES.ADMINISTRATION]: 'Administration',
    [ROUTES.AI]: 'AI',
    [ROUTES.AUTH_CALLBACK]: 'Dashboard',
  };
  return map[pathname] || 'Dashboard';
}

// Derive a human role label from the in-memory session snapshot booleans.
export function roleLabel(u: any): string {
  if (!u) return 'User';
  if (u.is_admin) return 'Administrator';
  if (u.is_sponsor) return 'Sponsor';
  if (u.is_departmentOwner) return 'Department Owner';
  if (u.is_systemOwner) return 'System Owner';
  if (u.is_resourceOwner) return 'Resource Owner';
  if (u.is_cloudAccountOwner) return 'Cloud Account Owner';
  if (u.is_technical) return 'Technical User';
  if (u.is_business) return 'Business User';
  return 'User';
}

export function initialsFor(name?: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name || 'U').slice(0, 2).toUpperCase();
}
